import React, { useState, useEffect, useContext } from "react";
import { useRouter } from "next/router";
import ChatMessageKit, {
  ChatMessageEnums,
  ChatMessageCollection,
  ChatTypeEnums,
} from "../../data/ChatMessageKit";
import { ChatContext } from "../../contexts/ChatContext";
import {
  get,
  limitToLast,
  off,
  onChildAdded,
  onChildChanged,
  onValue,
  orderByChild,
  query,
  ref,
} from "firebase/database";

export default function ChatWindow({
  urlParams,
  type,
  me,
  memberList = [],
  order,
  listItem = false,
  onListItemClick = () => null,
  listItemTitle,
  onSetUnreadMessageCount = () => {},
  unreadMessagesFilterFunction = () => true,
  oldestLastActiveTimestamp = () => 0,
  onSetLatestMessage = () => {},
  forceOpen = false,
  filter,
  isCorporateAgent = false,
  isChatActive,
  eventUserAvailabilityList = null,
  children = () => {},
}) {
  const MAX_MESSAGES = listItem ? 10 : 500;
  const router = useRouter();

  const { setUnreadMessages } = useContext(ChatContext);

  const [firebaseMessageList, setFirebaseMessageList] = useState([]);
  const [hasLoadedInitialMessages, setHasLoadedInitialMessages] =
    useState(false);
  const [latestMessage, setLatestMessage] = useState(null);
  const [lastActive, setLastActive] = useState(null);
  // const [clientAvailability, setClientAvailability] = useState(null)
  const [tabIsOpen, setTabIsOpen] = useState(false);
  const [shouldForceOpen, setShouldForceOpen] = useState(forceOpen);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [messageCollection, setMessageCollection] = useState(
    new ChatMessageCollection({
      me,
      messageList: [],
      urlParams,
      type: type,
    })
  );
  // const [firebaseRef, setFirebaseRef] = useState(messageCollection.getTypedRef())
  const [firebaseCleanupFuncs, setFirebaseCleanupFuncs] = useState([]);
  const [mutedList, setMutedList] = useState({});
  const { eventSlug } = urlParams;
  const [videoRoomSlug, setVideoRoomSlug] = useState(null);
  const [hostRoomUrl, setHostRoomUrl] = useState(null);
  const [videoBtn, setVideoBtn] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(true);
  const [claimedBy, setClaimedBy] = useState(null);
  const [tabTitle, setTabTitle] = useState("");
  const [corporateChatAvailability, setCorporateChatAvailability] = useState(false)
  const [CAeventUserLastActive, setCAeventUserLastActive] = useState(null);

  const onCreateVideoRoom = () => {
    setVideoBtn(true);
    if (type === ChatTypeEnums.PRIVATE) {
      if (videoRoomSlug) {
        router.push(`/${eventSlug}/${videoRoomSlug}`);
      } else {
        ChatMessageKit.createPrivateVideoroom(urlParams).then(
          (videoRoomSlug) => {
            sendVideoMessage(videoRoomSlug);
            setVideoBtn(false);
          }
        );
      }
    }

    if (isCorporate()) {
      if (videoRoomSlug) {
        if (isCorporateAgent) {
          router.push({
            pathname: `/agents/${urlParams.organizationSlug}/${urlParams.eventSlug}/videoroom/${videoRoomSlug}`,
            query: { hostRoomUrl: hostRoomUrl },
          });
        } else {
          router.push(`/${eventSlug}/${videoRoomSlug}`);
        }
      } else {
        ChatMessageKit.createCorporateVideoroom({ ...urlParams }).then(
          (videoRoomSlug) => {
            sendVideoMessage(videoRoomSlug);
            setVideoBtn(false);
          }
        );
      }
    }
  };

  const sendVideoMessage = (videoRoomSlug) => {
    messageCollection.create("Virtual room created!", {
      kind: ChatMessageEnums.VIDEO,
      videoRoomSlug,
      isAgent: isCorporateAgent,
    });
  };

  const setLastActiveFB = () => {
    if (type === ChatTypeEnums.PRIVATE) {
      ChatMessageKit.setLastActive({
        ...urlParams,
        ...me,
      });
    }

    if (type === ChatTypeEnums.CORPORATE && !isCorporateAgent) {
      ChatMessageKit.setCorporateClientLastActive({
        ...urlParams,
        ...me,
      });
    }

    if (getIsCorporateAgent()) {
      ChatMessageKit.setCorporateAgentLastActive({
        ...urlParams,
        ...me,
      });
    }
  };

  const computeUnreadMessageCount = () => {
    let unreadMessages = messageCollection.messageList.filter(
      (message) => message.epoch > lastActive
    );

    unreadMessages = unreadMessages.filter(unreadMessagesFilterFunction);

    setUnreadMessageCount(unreadMessages.length);
  };

  const removeDuplicateMessages = (messageList) => {
    const filteredMessageList = [];

    messageList.forEach((message) => {
      const messageInList = filteredMessageList.some(
        (filteredMessage) => filteredMessage[0] === message[0]
      );
      if (!messageInList) filteredMessageList.push(message);
    });

    return filteredMessageList;
  };

  const computeLatestMessage = (messageList) => {
    if (messageList.length > 0) {
      setLatestMessage(messageList[messageList.length - 1][1]);
    }
  };

  function archiveChat() {
    ChatMessageKit.setUserInboxLastActivity({
      ...urlParams,
      ...me,
      lastActive: oldestLastActiveTimestamp - 1,
      isArchived: true,
    });
  }

  function isCorporate() {
    return type === ChatTypeEnums.CORPORATE;
  }

  function getIsCorporateAgent() {
    return type === ChatTypeEnums.CORPORATE && isCorporateAgent;
  }

  function handleToggleClaimedBy() {
    if (getIsCorporateAgent()) {
      const payload = claimedBy
        ? false
        : {
            displayName: me.displayName,
            userId: me.user.id,
          };

      ChatMessageKit.setCorporateChatClaimedBy({
        ...urlParams,
        claimedBy: payload,
      });
    }
  }

  function handleSetClaimedByIfFalse() {
    if (getIsCorporateAgent() && !claimedBy) {
      const payload = {
        displayName: me.displayName,
        userId: me.user.id,
      };

      ChatMessageKit.setCorporateChatClaimedBy({
        ...urlParams,
        claimedBy: payload,
      });
    }
  }

  function handleSetAgentLatestActivity() {
    ChatMessageKit.setCorporateAgentLatestActivity({
      ...urlParams,
      agentId: me.user.id,
    });
  }

  function onlyHasInitialMessage() {
    const { messageList } = messageCollection;
    const messageCount = messageList.length;
    return messageCount == 1 ? messageList[0].isAgent : false;
  }

  ////////////////////////////////////////////////////////////////
  // Message listeners
  ////////////////////////////////////////////////////////////////

  function bindAddUpdateFirebaseListeners() {
    const collectionRef = messageCollection.getTypedRef();

    const latestMessageRef = query(collectionRef, limitToLast(1));
    onChildAdded(latestMessageRef, (snapshot) => {
      if (!messageCollection.messageExists(snapshot.key)) {
        const newMessage = [snapshot.key, snapshot.val()];

        setFirebaseMessageList((currState) => [...currState, newMessage]);
      }
    });

    onChildChanged(collectionRef, (snapshot) => {
      const updatedMessage = [snapshot.key, snapshot.val()];

      setFirebaseMessageList((currState) =>
        currState.map((message, i) => {
          const messageIndex = currState.findIndex(
            (message) => message[0] == updatedMessage[0]
          );
          return i == messageIndex ? updatedMessage : message;
        })
      );
    });
  }

  useEffect(() => {
    bindAddUpdateFirebaseListeners();
  }, []);

  ////////////////////////////////////////////////////////////////
  // MessageCollection updates
  ////////////////////////////////////////////////////////////////

  useEffect(() => {
    setMessageCollection(() => {
      setHasLoadedInitialMessages(true);
      return new ChatMessageCollection({
        me,
        messageList: removeDuplicateMessages(firebaseMessageList) || [],
        urlParams,
        type,
      });
    });

    if (hasLoadedInitialMessages) computeLatestMessage(firebaseMessageList);
  }, [firebaseMessageList]);

  useEffect(() => {
    if (isCorporateAgent && latestMessage) {
      ChatMessageKit.setCorporateInboxLatestActivity({
        ...urlParams,
        latestActivity: latestMessage?.epoch,
      });
    }

    onSetLatestMessage(latestMessage);
  }, [latestMessage]);

  ////////////////////////////////////////////////////////////////
  // Unread Message Count computation
  ////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (lastActive !== null) {
      if (type === ChatTypeEnums.PRIVATE) {
        if (!tabIsOpen) {
          computeUnreadMessageCount();
        } else {
          setUnreadMessageCount(0);
          setLastActiveFB();
        }
      }

      if (type === ChatTypeEnums.CORPORATE && !isCorporateAgent) {
        if (!tabIsOpen) {
          computeUnreadMessageCount();
        } else {
          setUnreadMessageCount(0);
          setLastActiveFB();
        }
      }

      // if (onlyHasInitialMessage()) {
      //   setShouldForceOpen(true);
      //   console.log("Forcing open", onlyHasInitialMessage(), Date.now());
      // }

      if (isCorporateAgent && !listItem) {
        setUnreadMessageCount(0);
        setLastActiveFB();
      }

      if (listItem) computeUnreadMessageCount();
    }
  }, [tabIsOpen, messageCollection]);

  useEffect(() => {
    if (
      type === ChatTypeEnums.CORPORATE &&
      !isCorporateAgent &&
      onlyHasInitialMessage()
    ) {
      setShouldForceOpen(true);
    }
  }, [messageCollection]);

  useEffect(() => {
    if (listItem) computeUnreadMessageCount();
  }, [lastActive]);

  useEffect(() => {
    if (typeof onSetUnreadMessageCount === "function")
      onSetUnreadMessageCount({
        inboxUUID: urlParams.inboxUUID,
        listItem,
        unreadMessageCount,
      });
  }, [unreadMessageCount, tabIsOpen]);

  ////////////////////////////////////////////////////////////////
  // Initial Firebase listeners
  ////////////////////////////////////////////////////////////////

  function bindInitialFirebaseListeners() {
    const collectionRef = messageCollection.getTypedRef();
    const messagesRef = query(collectionRef, limitToLast(MAX_MESSAGES));

    onValue(
      messagesRef,
      (snapshot) => {
        const snapshotVal = snapshot.val() || {};
        const newFirebaseMessageList = Object.entries(snapshotVal);
        setFirebaseMessageList(() => {
          return newFirebaseMessageList;
        });
      },
      { onlyOnce: true }
    );

    if (type === ChatTypeEnums.PUBLIC) {
      // Get list of muted users
      ChatMessageKit.getMutedList({
        ...urlParams,
        onChange: (snapshot) => {
          setMutedList(snapshot.val());
        },
      });
    }

    if (type === ChatTypeEnums.PRIVATE) {
      // Get video room settings
      const videoRoomSettingsCleanup = ChatMessageKit.listenToVideoroomSettings(
        {
          ...urlParams,
          onChange: (snapshot) => {
            const videoroomSettings = snapshot.val();

            if (videoroomSettings.videoRoomSlug) {
              setVideoRoomSlug(videoroomSettings.videoRoomSlug);
            }
          },
        }
      );

      // Get last time user was active when chat is private
      const lastActiveCleanup = ChatMessageKit.listenToLastActive({
        ...urlParams,
        ...me,
        onChange: (snapshot) => {
          const settings = snapshot.val();

          if (typeof settings.lastActive !== undefined)
            setLastActive(settings.lastActive);
        },
      });

      setFirebaseCleanupFuncs((prevState) => [
        ...prevState,
        lastActiveCleanup,
        videoRoomSettingsCleanup,
      ]);
    }

    if (type === ChatTypeEnums.CORPORATE && !getIsCorporateAgent()) {
      const videRoomSettingsCleanup =
        ChatMessageKit.getCorporateVideoroomSettings({
          ...urlParams,
          onChange: (snapshot) => {
            const videoroomSettings = snapshot.val();

            if (videoroomSettings?.videoRoomSlug) {
              setVideoRoomSlug(videoroomSettings?.videoRoomSlug);
            }
          },
        });

      const clientLastActiveCleanup =
        ChatMessageKit.getCorporateClientLastActive({
          ...urlParams,
          onChange: (snapshot) => setLastActive(snapshot.val()),
        });

      const tabChatTitleCleanup = ChatMessageKit.getCorporateChatTitle({
        ...urlParams,
        onChange: (snapshot) => setTabTitle(snapshot.val()),
      });

      const tabChatOnlineCleanup = ChatMessageKit.getCorporateChatOnline({
        ...urlParams,
        onChange: (snapshot) => setCorporateChatAvailability(snapshot.val()),
      });

      // const tabTitleCleanup = ChatMessageKit.getCorporateInboxTabTitle({
      //   ...urlParams,
      //   onChange: (snapshot) => setTabTitle(snapshot.val()),
      // });

      setFirebaseCleanupFuncs((prevState) => [
        ...prevState,
        clientLastActiveCleanup,
        videRoomSettingsCleanup,
        tabChatTitleCleanup,
        tabChatOnlineCleanup,
        // tabTitleCleanup,
      ]);
    }

    if (getIsCorporateAgent()) {
      const claimedByCleanup = ChatMessageKit.getCorporateChatClaimedBy({
        ...urlParams,
        onChange: (snapshot) => setClaimedBy(snapshot.val()),
      });

      const agentLastActiveCleanup = ChatMessageKit.getCorporateAgentLastActive(
        {
          ...urlParams,
          onChange: (snapshot) => {
            if (snapshot.val() !== null) setLastActive(snapshot.val());
          },
        }
      );

      const eventUserLastActiveCleanup =
        ChatMessageKit.getCorporateClientLastActive({
          ...urlParams,
          onChange: (snapshot) => {
            if (snapshot.val() !== null)
              setCAeventUserLastActive([urlParams.eventUserId, snapshot.val()]);
          },
        });

      const videoRoomSettingsCleanup =
        ChatMessageKit.getCorporateVideoroomSettings({
          ...urlParams,
          onChange: (snapshot) => {
            const videoroomSettings = snapshot.val();

            if (videoroomSettings?.videoRoomSlug) {
              setVideoRoomSlug(videoroomSettings?.videoRoomSlug);
            }

            if (videoroomSettings?.hostRoomUrl) {
              setHostRoomUrl(videoroomSettings?.hostRoomUrl);
            }
          },
        });

      setFirebaseCleanupFuncs((prevState) => [
        ...prevState,
        eventUserLastActiveCleanup,
        claimedByCleanup,
        agentLastActiveCleanup,
        videoRoomSettingsCleanup,
      ]);
    }
  }

  function firebaseCleanup() {
    off(messageCollection.getTypedRef());
    firebaseCleanupFuncs.forEach((func) => func());
  }

  useEffect(() => {
    bindInitialFirebaseListeners();
  }, []);

  useEffect(() => {
    // if(listItem) {
    firebaseCleanup();
    bindInitialFirebaseListeners();
    bindAddUpdateFirebaseListeners();
    // }

    return () => firebaseCleanup();
  }, [urlParams]);

  useEffect(() => {
    setShouldForceOpen(forceOpen);
  }, [forceOpen]);

  function getChatIdentifier() {
    if (type === ChatTypeEnums.CORPORATE) return "corporate";
    if (type === ChatTypeEnums.PRIVATE) return urlParams.inboxUUID;
  }

  useEffect(() => {
    if (type === ChatTypeEnums.CORPORATE || type === ChatTypeEnums.PRIVATE) {
      setUnreadMessages((prevState) => {
        return {
          ...prevState,
          [getChatIdentifier()]: unreadMessageCount,
        };
      });
    }
  }, [unreadMessageCount]);

  const getFormattedUnreadMessageCount = () =>
    unreadMessageCount > MAX_MESSAGES - 1
      ? `+${MAX_MESSAGES - 1}`
      : unreadMessageCount;

  const listItemClick = () => {
    setUnreadMessageCount(0);
    setLastActiveFB();
    onListItemClick();
  };

  return (
    <>
      {children({
        unreadMessageCount,
        urlParams,
        type,
        me,
        memberList,
        oldestLastActiveTimestamp,
        messageCollection,
        isCorporateAgent,
        claimedBy,
        handleSetClaimedByIfFalse,
        handleSetAgentLatestActivity,
        onCreateVideoRoom,
        videoBtn,
        hostRoomUrl,
        eventUserAvailabilityList,
        mutedList,
        tabIsOpen,
        setTabIsOpen,
        order,
        archiveChat,
        forceOpen: shouldForceOpen,
        tabTitle,
        corporateChatAvailability,
        listItemTitle,
        onListItemClick: () => {
          listItemClick();
        },
        handleToggleClaimedBy,
        isChatActive,
        getFormattedUnreadMessageCount,
      })}
    </>
  );
}
